import { z } from 'zod';
/** 后端时间字段兼容 ISO 字符串、时间戳和 Jackson 数组形式。 */
export const assetTimeSchema = z
    .union([z.string(), z.number(), z.array(z.number())])
    .nullable()
    .optional();
/** 素材媒体类型编码；5、6 仅用于读取旧数据，新建素材只使用图片、视频和音频编码。 */
export const assetDataTypeSchema = z.enum(['2', '3', '4', '5', '6']);
/** 素材来源类型编码：1-AI 生成，2-用户上传，3-分镜合成。 */
export const assetSourceTypeSchema = z.enum(['1', '2', '3']);
/** 素材业务分类编码：1-剧情素材，2-游戏素材，3-官方素材。 */
export const assetCategorySchema = z.enum(['1', '2', '3']);
/** 素材主体类型编码：1-角色，2-场景，3-道具。 */
export const assetSubjectTypeSchema = z.enum(['1', '2', '3']);
/** 项目素材库的素材归属；官方素材为平台资产，用户不可上传。 */
export const materialScopeSchema = z.enum(['story', 'game', 'official']);
/** 图片主体分类。 */
export const materialSubjectTypeSchema = z.enum(['character', 'scene', 'prop']);
/** 素材引用位置：引用素材的项目、章节与剧情节点，名称由后端保存的名称快照。 */
export const assetUsageReferenceSchema = z.object({
    /** 引用素材的项目 ID。 */
    projectId: z.number().nullable().optional(),
    /** 引用素材的项目名称快照；项目已不存在时为空。 */
    projectName: z.string().nullable().optional(),
    /** 引用素材的章节 ID（cg_script_node.id）；为空表示项目级引用。 */
    chapterId: z.number().nullable().optional(),
    /** 引用素材的章节名称快照；项目级引用为空。 */
    chapterName: z.string().nullable().optional(),
    /** 引用素材的剧情节点 ID；为空表示项目级引用。 */
    nodeId: z.number().nullable().optional(),
    /** 引用素材的节点名称快照；项目级引用为空。 */
    nodeName: z.string().nullable().optional(),
});
/** 视频时间轴缩略图生成状态。 */
export const assetThumbnailStatusSchema = z.enum(['processing', 'ready', 'failed']);
/**
 * 分镜参考资产条目。
 * 顺序即分镜提示词里参考图占位符的序号，第 1 项对应提示词里的第 1 张参考图。
 */
export const storyboardAssetRefSchema = z.object({
    /** 参考资产 ID。 */
    assetId: z.number(),
    /** 参考资产名称快照，与提示词里的占位名一致。 */
    name: z.string().nullable().optional(),
    /** 参考资产媒体类型：2-图片，3-视频，4-音频；资产已删除时为空。 */
    assetDataType: assetDataTypeSchema.nullable().optional(),
    /** 参考资产文件地址；还没有产出文件时为空。 */
    assetUrl: z.string().nullable().optional(),
    /** 参考资产封面地址；没有封面时为空。 */
    coverUrl: z.string().nullable().optional(),
});
/** 分类选项中的单个枚举项；code 为接口编码，name 为展示名称。 */
export const assetClassificationOptionSchema = z.object({
    code: z.string(),
    name: z.string(),
    /** 展示顺序，数值越小越靠前；历史载荷可能缺省。 */
    order: z.number().int().nullable().optional(),
});
/** 素材分类选项结构，用于按服务端顺序渲染筛选项与生成展示文案。 */
export const assetClassificationOptionsSchema = z.object({
    /** 素材分类：剧情、游戏、官方。 */
    categories: assetClassificationOptionSchema.array().default([]),
    /** 媒体类型：视频、图片、音频。 */
    dataTypes: assetClassificationOptionSchema.array().default([]),
    /** 图片主体类型：角色、场景、道具。 */
    subjectTypes: assetClassificationOptionSchema.array().default([]),
    /** 素材来源：AI 生成、用户上传、分镜合成。 */
    sourceTypes: assetClassificationOptionSchema.array().default([]),
    /** 必须选择主体类型的媒体编码，当前固定为图片 2。 */
    subjectRequiredDataType: z.string().nullable().optional(),
});
/** 素材中心卡片与详情结构，所有接口数据进入页面前必须完成校验。 */
export const assetItemSchema = z.object({
    id: z.number(),
    assetName: z.string().nullable().optional(),
    projectId: z.number().nullable().optional(),
    projectName: z.string().nullable().optional(),
    taskId: z.string().nullable().optional(),
    userId: z.number(),
    /** 创建人展示名；后端按昵称优先、姓名与业务 UID 依次兜底，都取不到时为空。 */
    userName: z.string().nullable().optional(),
    assetType: assetSourceTypeSchema,
    assetDataType: assetDataTypeSchema,
    /** 素材业务分类；服务端列为 NOT NULL，仅在历史与测试载荷中可能缺省。 */
    assetCategory: assetCategorySchema.nullable().optional(),
    /** 素材主体类型；无特定主体（如视频、音频）时为空。 */
    assetSubjectType: assetSubjectTypeSchema.nullable().optional(),
    /** 项目素材库归属；旧全局素材记录可能没有该字段。 */
    materialScope: materialScopeSchema.nullable().optional(),
    /** 图片主体分类；仅图片素材有值。 */
    subjectType: materialSubjectTypeSchema.nullable().optional(),
    /** 当前素材的有效引用位置；没有引用时后端返回空数组。 */
    references: assetUsageReferenceSchema.array().optional(),
    /**
     * 本素材作为分镜占位素材时的参考资产清单。
     * 只有分镜拆分产出的素材有值，普通素材不返回该字段。
     */
    assetRefs: storyboardAssetRefSchema.array().optional(),
    asset: z.string().nullable().optional(),
    coverUrl: z.string().nullable().optional(),
    duration: z.number().nullable().optional(),
    /** 像素宽度：服务端不返回，编辑器按素材地址实测补齐。 */
    width: z.number().nullable().optional(),
    /** 像素高度：服务端不返回，编辑器按素材地址实测补齐。 */
    height: z.number().nullable().optional(),
    /** 素材文件体积（字节）；后端接口尚未返回，mock 演示数据会填充用于详情展示。 */
    fileSize: z.number().int().nonnegative().nullable().optional(),
    description: z.string().nullable().optional(),
    /** 生成这条素材时用户实际输入的提示词；服务端按 taskId 从 ai_work 反查，上传素材与未绑任务时为空。 */
    prompt: z.string().nullable().optional(),
    generationPrompt: z.string().nullable().optional(),
    tags: z.array(z.string()).default([]),
    isCollect: z.enum(['0', '1']),
    /** 逻辑删除标记：0-未删除，1-已删除；已删除素材在编辑器展示为资源已丢失。 */
    isDeleted: z.number().int().optional(),
    /** 视频时间轴缩略图状态：processing-抽帧中，ready-可用，failed-失败；非视频素材为空。 */
    thumbnailStatus: assetThumbnailStatusSchema.nullable().optional(),
    /** 视频时间轴缩略图胶片图地址；仅 ready 状态返回。 */
    thumbnailUrl: z.string().nullable().optional(),
    /** 视频时间轴缩略图版本号。 */
    thumbnailVersion: z.number().nullable().optional(),
    createTime: assetTimeSchema,
    updateTime: assetTimeSchema,
});
/** 素材分页响应结构。 */
export const assetPageSchema = z.object({
    current: z.number(),
    size: z.number(),
    total: z.number(),
    pages: z.number(),
    records: assetItemSchema.array(),
});
/** 素材视频时间轴缩略图状态结构。 */
export const assetThumbnailSchema = z.object({
    assetId: z.number(),
    status: assetThumbnailStatusSchema,
    /** 横向胶片图地址；仅 ready 状态返回。 */
    thumbnailUrl: z.string().nullable().optional(),
    /** 缩略图版本号，用于拼接缓存刷新参数。 */
    version: z.number().nullable().optional(),
    /** 胶片图包含的帧数。 */
    frameCount: z.number().nullable().optional(),
    /** 胶片图单帧宽度（像素）。 */
    frameWidthPx: z.number().nullable().optional(),
    /** 最近一次失败原因；非 failed 状态为空。 */
    errorMessage: z.string().nullable().optional(),
});
/**
 * 素材创建人筛选项。
 *
 * 服务端按项目聚合未删除素材的创建人，前端「创建人」下拉选中后把 userId 回传给分页接口。
 */
export const assetCreatorOptionSchema = z.object({
    /** 创建人用户 ID。 */
    userId: z.number(),
    /** 创建人展示名；服务端已在取不到昵称时回退成「用户 {id}」。 */
    userName: z.string().nullable().optional(),
    /** 该创建人在当前项目下的素材数量。 */
    assetCount: z.number(),
});
/**
 * 读取素材当前的有效引用数量。
 *
 * 后端只返回引用位置列表，没有独立计数字段，引用数量统一由列表长度派生，
 * 避免各页面各自判空导致口径不一致。
 * @param asset 素材实体。
 * @returns 引用数量；后端未返回引用列表时为 0。
 */
export function resolveAssetUsageCount(asset) {
    return asset.references?.length ?? 0;
}
/**
 * 解析素材创建人的展示名。
 *
 * 后端 AssetItemVO 只返回 userName（昵称优先，姓名与业务 UID 依次兜底），
 * 取不到时统一回退成 `用户 {userId}`，避免各页面各自判空导致口径不一致。
 * @param asset 素材实体。
 * @returns 可直接展示的创建人文案。
 */
export function resolveAssetCreatorName(asset) {
    const name = asset.userName?.trim();
    return name && name.length > 0 ? name : `用户 ${String(asset.userId)}`;
}
